import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Mutation_Root, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  fetchKapProgramApplicationCommentQuery,
  updateKapProgramApplicationCommentMutation,
} from 'src/screens/shared/application/details/application-information/applicationInformationQueries'
import { SummaryCommentEditForm, SummaryCommentFormValues } from 'src/screens/shared/common/SummaryCommentEditForm'
import { usePermissionsForKapProgram } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

type originType = 'SAVE' | 'SAVE_AND_BACK'

export const KapApplicationSummaryEditPage = () => {
  const { programId } = useParams()
  const program_id = parseInt(programId as string)

  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const originRef = useRef<originType>('SAVE')

  const [applicationSummaryComment, setApplicationSummaryComment] = useState<SummaryCommentFormValues>()

  const { loading, canEdit } = usePermissionsForKapProgram(program_id)

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await urqlClient
        .query<{ kap_program_by_pk: Query_Root['kap_program_by_pk'] }>(fetchKapProgramApplicationCommentQuery, {
          id: program_id,
        })
        .toPromise()

      if (data && data.kap_program_by_pk) {
        setApplicationSummaryComment({
          comment: data.kap_program_by_pk.application_comment || null,
        })
      } else {
        notificationService.operationFailed()
      }
    }

    fetchData()
  }, [urqlClient, program_id, notificationService])

  const onBack = () => {
    const route = ROUTES.KapApplicationDetailsRoot.nested.Summary.params({ programId })
    navigate(`${route}#comments`)
  }

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('edit-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const updateHandler = async (values: SummaryCommentFormValues) => {
    const { error, data } = await urqlClient
      .mutation<{ update_kap_program: Mutation_Root['update_kap_program'] }>(
        updateKapProgramApplicationCommentMutation,
        {
          id: program_id,
          applicationComment: HtmlSanitizer.sanitize(values.comment ?? ''),
        },
      )
      .toPromise()

    if (error || data?.update_kap_program?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else {
      notificationService.changesSaved()
      setApplicationSummaryComment({ comment: values.comment })
      originRef.current === 'SAVE_AND_BACK' && onBack()
    }
  }

  if (!loading && !canEdit) {
    return <NotAuthorized inEditPage />
  }

  return (
    <ScreenLayout
      title={getMessage('label.title.edit.comments')}
      onBack={onBack}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <>
          {!loading && applicationSummaryComment && (
            <>
              <HelpAndInstructions labelKey="label.help.kap.application.summary.comments" defaultExpansion />
              <SummaryCommentEditForm comment={applicationSummaryComment.comment} onSave={updateHandler} />
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
